import React, { useState, useMemo } from 'react'
import BlockchainImage from '../../../images/Blockchain.png'
import { generateReportPPTX } from './pptx/report'
import { exams as examsApi } from "../../../api/exams";
import { useQuery } from 'react-query';
import { report } from '../../../api/report';
import toast from 'react-hot-toast';
import Modal from '../survey/components/Modal';

const Report = () => {
    const storedBranding = useMemo(() => {
        try {
            return JSON.parse(sessionStorage.getItem("branding") || '{}');
        } catch (error) {
            return null;
        }
    }, []);
    const appearance = storedBranding?.data?.appearance;

    const [isExporting, setIsExporting] = useState(false)
    const [selectedSurvey, setSelectedSurvey] = useState<any>(null)
    const [showEligibilityModal, setShowEligibilityModal] = useState(false)
    const [reportReady, setReportReady] = useState(false)
    const user = JSON.parse(sessionStorage.getItem('user') || '{}')
    const { isLoading: surveysLoading, data: surveys, } = useQuery(
        ["user surveys",],
        async () => {
            const data = await examsApi.getSurveys({
                type: null, id: user?.user?.user_id, currentPage: 1, perPage: 10000, search: ''
            });
            return data;
        },
        {
            select: (data) => data,
            refetchOnWindowFocus: false,
        }
    );

    const { isLoading: participantReportLoading, data: participantReport, refetch } = useQuery(
        ["get participant report", selectedSurvey?.assessment?.id, user?.user?.user_id, selectedSurvey?.user_exam_id],
        async () => {
            const data = await report.getParticipantReport({
                assessmentId: selectedSurvey?.assessment?.id,
                userId: user?.user?.user_id,
                useExamId: selectedSurvey?.user_exam_id
            })
            return data;
        },
        {
            select: (data) => data,
            refetchOnWindowFocus: false,
            enabled: !!selectedSurvey,
            onError: (error: any) => {
                if (error?.response?.status === 400) {
                    setShowEligibilityModal(true)
                    setReportReady(false)
                }
            },
            onSuccess: (data) => {
                setShowEligibilityModal(false)
                setReportReady(true)
            },
            retry: false
        }
    );

    const handleExportReport = async () => {
        if (!selectedSurvey?.assessment?.id || !selectedSurvey?.user_exam_id) {
            toast.error('Please select a survey first')
            return
        }
        if (!reportReady) {
            //  setShowEligibilityModal(true)
            refetch()
            return
        }
        try {
            setIsExporting(true)
            toast.loading('Generating your report...')
            // You can customize this data based on your actual user data
            const reportData = {
                candidateName: `${user?.user?.name}` || 'User',
                surveyTitle: selectedSurvey?.title || selectedSurvey?.name || '360-Degree Feedback Report',
                date: new Date().toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                }),
                participantReport
            }
            const fileName = await generateReportPPTX(reportData)
            toast.remove()
            toast.success(`Report exported successfully: ${fileName}`)
        } catch (error) {
            console.error('Error exporting report:', error)
            toast.remove()
            toast.error('Failed to export report. Please try again.')
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12 animate-fadeIn">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        360 Degree Feedback Report
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Your comprehensive performance summary
                    </p>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
                    {/* Blockchain Image Section */}
                    <div className="relative bg-primary p-8 md:p-12" style={{ backgroundColor: appearance?.theme_colour }}>
                        <div className="flex justify-center items-center">
                            <div className="relative group">
                                <div className="absolute -inset-1  rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
                                <img
                                    src={BlockchainImage}
                                    alt="Blockchain Security"
                                    className="relative w-full max-w-md h-auto rounded-lg shadow-2xl transform transition-transform duration-300 group-hover:scale-105"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 md:p-12">
                        <div className="space-y-6">
                            {/* Description */}
                            <div className="text-center space-y-4">
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                                    Secure & Transparent Feedback
                                </h2>
                            </div>
                            {/* Survey Selection */}
                            <div className="flex flex-col items-center max-w-md mx-auto space-y-3">
                                <label htmlFor="survey-select" className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                    Select Feedback Survey
                                </label>
                                <div className="relative w-full">
                                    <select
                                        id="survey-select"
                                        value={selectedSurvey?.assessment?.id}
                                        onChange={(e) => {
                                            const survey = surveys?.data?.find((survey: any) => survey.assessment?.id === e.target.value)
                                            setSelectedSurvey(survey)
                                        }}
                                        disabled={surveysLoading}
                                        className="w-full bg-gray-50 border-2 border-gray-100 text-gray-900 text-lg rounded-xl block p-3.5 appearance-none hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            borderColor: appearance?.theme_colour
                                        }}
                                    >
                                        <option value="">Choose a survey to export report...</option>
                                        {surveys?.data?.map((survey: any) => (
                                            <option key={survey.assessment?.id} value={survey.assessment?.id}>
                                                {survey?.assessment?.
                                                    preferred_exam_title || 'Untitled Survey'}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
                                        <svg className="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                        </svg>
                                    </div>
                                </div>
                                {surveysLoading && (
                                    <p className="text-xs text-primary animate-pulse font-medium" style={{ color: appearance?.theme_colour }}>Loading your surveys...</p>
                                )}
                            </div>

                            {/* Export Button */}
                            <div className="flex justify-center">
                                <button
                                    onClick={handleExportReport}
                                    disabled={isExporting || surveysLoading || participantReportLoading}
                                    className="group relative inline-flex items-center gap-3 px-8 md:px-16 py-4 bg-primary text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    style={{ backgroundColor: appearance?.theme_colour }}
                                >
                                    {isExporting || surveysLoading || participantReportLoading ? (
                                        <>
                                            <svg
                                                className="w-6 h-6 animate-spin"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                />
                                            </svg>
                                            <span>Generating...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-6 h-6 transform group-hover:translate-y-1 transition-transform duration-300"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <span>Export Report</span>
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Additional Info */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <p className="text-center text-sm text-gray-500">
                                    Report generated on {new Date().toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showEligibilityModal && (
                <Modal closeModal={setShowEligibilityModal} title="Eligibility notice">
                    <div className="py-4">
                        <p className="text-gray-700 text-lg">
                            Report unavailable; some required raters did not submit feedback.
                        </p>
                    </div>
                </Modal>
            )}

            {/* Custom Animation Styles */}
            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.6s ease-out;
                }
            `}</style>
        </div>
    )
}

export default Report